import PressureRaw from "@/components/PressureRaw";
import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { fmtDate, metricLabel } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Download, Database, Loader2, Braces } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";
import { useRefresh } from "@/context/RefreshContext";

const num = (value) => (value == null ? "" : Number(value).toLocaleString("nl-NL", { maximumFractionDigits: 4 }));

export default function RawData() {
  const { t, lang } = useLang();
  const { tick } = useRefresh();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedDevice = searchParams.get("device");
  const cycleId = searchParams.get("cycle");
  const [mode, setMode] = useState("processed");
  const [devices, setDevices] = useState([]);
  const [deviceId, setDeviceId] = useState("");
  const [definitions, setDefinitions] = useState([]);
  const [processedRows, setProcessedRows] = useState([]);
  const [protocolRows, setProtocolRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [nextProcessed, setNextProcessed] = useState(null);
  const [nextProtocol, setNextProtocol] = useState(null);

  useEffect(() => {
    api.get("/devices").then((response) => {
      setDevices(response.data);
      const linked = response.data.find((device) => device.id === requestedDevice);
      if (linked) setDeviceId(linked.id);
      else if (response.data[0]) setDeviceId(response.data[0].id);
    });
  }, [requestedDevice]);

  const loadProcessed = useCallback(async (id, before) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/devices/${id}/raw`, { params: { limit: 300, before, cycle_id: cycleId || undefined } });
      setDefinitions(data.metrics || []);
      setProcessedRows((previous) => (before ? [...previous, ...data.rows] : data.rows));
      setNextProcessed(data.rows.length < 300 ? null : data.next);
    } catch {
      toast.error(t("De verwerkte meetdata kon niet worden geladen.", "Processed measurement data could not be loaded."));
    } finally {
      setLoading(false);
    }
  }, [cycleId, t]);

  const loadProtocol = useCallback(async (id, before) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/devices/${id}/protocol-records`, { params: { limit: 100, before } });
      setProtocolRows((previous) => (before ? [...previous, ...data.rows] : data.rows));
      setNextProtocol(data.next || null);
    } catch {
      toast.error(t("De onbewerkte protocolrecords konden niet worden geladen.", "Raw protocol records could not be loaded."));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    if (!deviceId || deviceId.startsWith("PR-")) return;
    if (mode === "processed") {
      setProcessedRows([]);
      setNextProcessed(null);
      loadProcessed(deviceId, null);
    } else {
      setProtocolRows([]);
      setNextProtocol(null);
      loadProtocol(deviceId, null);
    }
  }, [deviceId, mode, loadProcessed, loadProtocol]);

  useEffect(() => {
    if (!tick || !deviceId || deviceId.startsWith("PR-")) return;
    if (mode === "processed") loadProcessed(deviceId, null);
    else loadProtocol(deviceId, null);
  }, [tick]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedDevice = devices.find((device) => device.id === deviceId);
  const rows = mode === "processed" ? processedRows : protocolRows;

  const exportData = () => {
    if (!rows.length) return;
    let blob;
    let filename;
    if (mode === "processed") {
      const columns = ["timestamp", ...definitions.map((metric) => metric.key)];
      const lines = [columns.join(",")];
      processedRows.forEach((row) => lines.push([row.ts, ...definitions.map((metric) => row.metrics?.[metric.key] ?? "")].join(",")));
      blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
      filename = `verwerkte-meetdata-${selectedDevice?.name || deviceId}.csv`;
    } else {
      blob = new Blob([JSON.stringify(protocolRows, null, 2)], { type: "application/json;charset=utf-8" });
      filename = `protocolrecords-${selectedDevice?.name || deviceId}.json`;
    }
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = filename;
    anchor.click();
    URL.revokeObjectURL(url);
    toast.success(mode === "processed" ? t("CSV geëxporteerd", "CSV exported") : t("Protocolrecords als JSON geëxporteerd", "Protocol records exported as JSON"));
  };

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="font-heading text-3xl font-black tracking-tight sm:text-4xl">{t("Ruwe data", "Raw data")}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t("Bekijk dezelfde apparaatdata als leesbare meetwaarden of rechtstreeks uit het Device API-protocol.", "View device data as readable measurements or directly from the Device API protocol.")}</p>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <Select value={deviceId} onValueChange={setDeviceId}>
            <SelectTrigger className="w-full sm:w-64" data-testid="raw-device-select"><SelectValue placeholder={t("Kies apparaat", "Select device")} /></SelectTrigger>
            <SelectContent>{devices.map((device) => <SelectItem key={device.id} value={device.id}>{device.name}</SelectItem>)}</SelectContent>
          </Select>
          {!deviceId.startsWith("PR-") && <Button variant="outline" className="gap-2" onClick={exportData} disabled={!rows.length} data-testid="export-raw-data">
            <Download className="h-4 w-4" /> {mode === "processed" ? "CSV" : "JSON"}
          </Button>}
        </div>
      </div>

      {deviceId.startsWith("PR-") ? <PressureRaw key={deviceId} deviceId={deviceId}/> : <>
      <Tabs value={mode} onValueChange={setMode}>
        <TabsList className="h-auto w-full justify-start gap-1 p-1 sm:w-auto" data-testid="raw-data-mode">
          <TabsTrigger value="processed" className="gap-2 py-2"><Database className="h-4 w-4" />{t("Verwerkte meetdata", "Processed measurement data")}</TabsTrigger>
          <TabsTrigger value="protocol" className="gap-2 py-2"><Braces className="h-4 w-4" />{t("Onbewerkte protocolrecords", "Raw protocol records")}</TabsTrigger>
        </TabsList>
      </Tabs>

      {cycleId && mode === "processed" && (
        <div className="flex flex-col gap-2 rounded-lg border border-primary/25 bg-primary/5 px-4 py-3 text-sm sm:flex-row sm:items-center sm:justify-between" data-testid="measurement-cycle-filter">
          <div><span className="font-bold">{t("Gekoppelde ruwe meetcyclus", "Linked raw measurement cycle")}</span><span className="ml-2 break-all font-mono text-xs text-muted-foreground">{cycleId}</span></div>
          <Button variant="ghost" size="sm" onClick={() => { searchParams.delete("cycle"); setSearchParams(searchParams); }}>{t("Alle meetdata tonen", "Show all measurement data")}</Button>
        </div>
      )}

      <div className="rounded-xl border border-border bg-card shadow-sm">
        <div className="flex flex-col gap-2 border-b border-border px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="font-heading text-base font-bold">{mode === "processed" ? t("Verwerkte meetdata", "Processed measurement data") : t("Onbewerkte protocolrecords", "Raw protocol records")}</h2>
            <p className="mt-1 text-xs text-muted-foreground">
              {mode === "processed"
                ? t("Meetrecords vertaald naar afzonderlijke ISO-, temperatuur- en sensorvelden.", "Measurement records translated into individual ISO, temperature and sensor fields.")
                : t("Records zoals de RevPi ze heeft aangeleverd, zonder omzetting naar afgeleide meetvelden.", "Records as supplied by the RevPi, without conversion into derived measurement fields.")}
            </p>
          </div>
          <span className="text-xs font-medium text-muted-foreground">{t(`${rows.length} ${mode === "processed" ? "metingen" : "records"} geladen`, `${rows.length} ${mode === "processed" ? "measurements" : "records"} loaded`)}</span>
        </div>

        {loading && rows.length === 0 ? (
          <div className="p-5"><Skeleton className="h-64 w-full" /></div>
        ) : mode === "processed" ? (
          <div className="max-h-[600px] overflow-auto">
            <Table data-testid="processed-data-table">
              <TableHeader className="sticky top-0 bg-card">
                <TableRow>
                  <TableHead className="sticky left-0 bg-card">{t("Datum & tijd", "Date & time")}</TableHead>
                  {definitions.map((metric) => <TableHead key={metric.key} className="whitespace-nowrap text-right">{metricLabel(metric.key, metric.label, lang)}</TableHead>)}
                </TableRow>
              </TableHeader>
              <TableBody>
                {processedRows.map((row, index) => (
                  <TableRow key={`${row.ts}-${index}`}>
                    <TableCell className="sticky left-0 bg-card whitespace-nowrap font-mono text-xs text-muted-foreground">{fmtDate(row.ts, lang)}</TableCell>
                    {definitions.map((metric) => <TableCell key={metric.key} className="text-right font-mono text-xs">{row.metrics?.[metric.key] == null ? "" : Number(row.metrics[metric.key]).toLocaleString(lang === "en" ? "en-GB" : "nl-NL", { maximumFractionDigits: 4 })}</TableCell>)}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <div className="max-h-[640px] overflow-auto">
            <Table data-testid="protocol-records-table">
              <TableHeader className="sticky top-0 bg-card">
                <TableRow>
                  <TableHead>{t("Ontvangen", "Received")}</TableHead>
                  <TableHead>{t("Recordtype", "Record type")}</TableHead>
                  <TableHead>Outbox-ID</TableHead>
                  <TableHead>Content-hash</TableHead>
                  <TableHead className="min-w-[420px]">{t("Oorspronkelijke payload", "Original payload")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {protocolRows.map((record) => (
                  <TableRow key={`${record.outbox_id}-${record.received_at}`} className="align-top">
                    <TableCell className="whitespace-nowrap font-mono text-xs text-muted-foreground">{fmtDate(record.received_at)}</TableCell>
                    <TableCell><span className="rounded bg-muted px-2 py-1 font-mono text-xs font-semibold">{record.record_type}</span></TableCell>
                    <TableCell className="max-w-56 break-all font-mono text-[11px]">{record.outbox_id}</TableCell>
                    <TableCell className="max-w-64 break-all font-mono text-[10px] text-muted-foreground">{record.content_hash}</TableCell>
                    <TableCell><pre className="max-w-[680px] whitespace-pre-wrap break-all rounded-md bg-muted/60 p-2 font-mono text-[11px] leading-4">{JSON.stringify(record.payload, null, 2)}</pre></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {((mode === "processed" && nextProcessed) || (mode === "protocol" && nextProtocol)) && (
          <div className="border-t border-border p-3 text-center">
            <Button variant="outline" size="sm" onClick={() => mode === "processed" ? loadProcessed(deviceId, nextProcessed) : loadProtocol(deviceId, nextProtocol)} disabled={loading} data-testid="load-more-raw-data">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : t("Meer laden", "Load more")}
            </Button>
          </div>
        )}
      </div>
      </>}
    </div>
  );
}
